<template>
  <v-app>
    <Sidebar v-if="user.ticket && user.id" />
    <Toolbar v-if="user.ticket" @changeLocale="onChangeLocale($event)" @refToolbar="onRefToolbar" />
    <ToolbarOff v-else />

    <v-main :class="user.ticket ? 'user' : 'login'" style="height:100vh;" fluid>
      <template v-if="user.ticket">
        <router-view v-if="isInit" :ref-toolbar="refToolbar" :key="$route.fullPath" fill-height/>
        <v-progress-linear
          v-else
          indeterminate
          color="light-green darken-4"
          height="10"
          striped
        ></v-progress-linear>
      </template>
      <template v-else>
        <Login />
      </template>
    </v-main>

    <v-dialog
      v-model="networkError"
      persistent
      width="50%"
      max-width="600px"
      class="dialog-z1000"
    >
      <v-card
        class="red lighten-4"
      >
        <v-card-text>
          <v-alert
            :value="true"
            type="error"
          >
            <!-- There was some network error. Please try refreshing the page or using another browser. -->
            <!-- {{ $t('ERROR_INTERNAL') }} -->
            {{ $t('ERROR_SERVER_FAILED') }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text small @click="$store.dispatch('clearNetworkError')">{{ $t('ERROR_CLOSE') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <AlertSnackBar></AlertSnackBar>
  </v-app>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
import AlertSnackBar from './components/AlertSnackBar'
import Sidebar from './components/Sidebar'
import Toolbar from './components/Toolbar'
import ToolbarOff from './components/ToolbarOff'
import Login from './components/Login'
import Data from './services/Data'
import Settings from './services/Settings'
import Folder from './services/Folder'
import Search from './services/Search'
import Tabview from './services/Tabview'
import Campaign from './services/Campaign'
import constants from './config/constants'
// import Statistics from './services/Statistics'

export default {
  name: 'App',
  components: {
    Sidebar,
    Toolbar,
    ToolbarOff,
    Login,
    AlertSnackBar,
  },
  props: ['oid', 'ticket', 'apiUrl', 'cmsUrl', 'appLang', 'crmUrl'],
  data () {
    return {
      refToolbar: null,
    }
  },
  computed: {
    // ...mapGetters(['user', 'isInit', 'isBusy', 'networkError'])
    ...mapGetters(['user', 'languages', 'isInit', 'networkError', 'lang'])
  },
  watch: {
    'user.ticket'(val) {
      if (!val) return;
      // TODO: use store subscriptions instead
      if(this.appLang) {
        this.$i18n.locale = this.appLang+'.UTF-8';
        this.$vuetify.lang.current = this.appLang.split("_")[0]
      } else {
        if (this.user.gui_lang_locale) {
          this.$i18n.locale = this.user.gui_lang_locale
        }
        if (this.user.gui_lang_id) {
          const lang = this.languages.find(a => a.id === this.user.gui_lang_id)
          if (lang) {
            this.$i18n.locale = lang.locale
            this.$vuetify.lang.current = lang.locale_lang
          }
        }
      }
      // check user ticket first
      new Settings(this.$api).load()
      .then(() => {
        // execute init only if ticket is valid
        this.init()
      })
      .catch(reason => {
        if (reason.status === 'session-expired') {
          this.$store.commit('loginError', constants.ERROR_TICKET_EXPIRED)
          this.logout()
        } else {
// console.error(reason)
        }
      })
    },
    languages(val) {
      if (this.user && this.user.ticket && !this.appLang) {
        const lang = val.find(a => a.id === this.user.gui_lang_id)
        if (lang) {
          this.$i18n.locale = lang.locale
          this.$vuetify.lang.current = lang.locale_lang
        }
      }
    },
    lang() {
      this.reloadSettings()
    },
  },
  mounted() {
    // store global params
    this.$store.commit('setAppApiUrl', this.apiUrl)
    this.$store.commit('setAppCmsUrl', this.cmsUrl)
    this.$store.commit('setAppCrmUrl', this.crmUrl)
    if(this.appLang) {
      this.$i18n.locale = this.appLang+'.UTF-8';
      this.$vuetify.lang.current = this.appLang.split("_")[0]
    }
    this.setLocaleDateFormat(this.$t('LOCALE_DATE_FORMAT'));
    // set API URL
    this.$api.setUrl(this.$store.getters.appApiUrl) // TODO: rework this with new API implementation ??
    if (this.$session.exists('user')) {
      try {
        const user = this.$session.get('user')
        this.setUser(user)
      } catch(e) {
        this.logout()
      }
    } else {
      this.$store.commit('clearUser')
    }
    if (this.oid) {
      this.$store.commit('setUserOID', this.oid)
    }
    else
    if (this.$route.query.oid) {
      this.$store.commit('setUserOID', this.$route.query.oid)
    }
    if (this.ticket) {
      this.$store.commit('setUserTicket', this.ticket)
    }
    else
    if (this.$route.query.ticket) {
      this.$store.commit('setUserTicket', this.$route.query.ticket)
    }
  },
  methods: {
    ...mapMutations([ 'setUser', 'setLocaleDateFormat' ]),
    init() {
      // load Nomenclature Data
      this.setupFields()
      new Data(this.$api).init()
      .catch(reason => {
        if (reason.code === 'MISSING_RIGHTS') {
          this.logout()
          this.$store.commit('loginError', 'ERROR_RIGHTS')
        } else {
// console.error(reason)
        }
      })
      
      // load Additional Contact Tabs
      new Tabview(this.$api).load()

      // load User Accounts
      new Data(this.$api).useraccounts()

      // load Campaigns
      new Campaign(this.$api).list()

      // load Folders (both for People and Campaigns)
      new Folder(this.$api).list()

      // load People Searches
      new Search(this.$api).list()

      // load Campaigns' Mailings
      new Campaign(this.$api).list_entries()

      // load Initial Statistics for Dashboard
      // new Statistics (this.$api).groupedCount()
    },
    setupFields() {
      // parse/set Master Fields
      let fields = require('@/config/fields.json')
      fields = fields.map(f => ({...f, label: this.$t(f['text'])}))
      this.$store.commit('setDataFields', fields)
    },
    logout() {
      this.$store.dispatch('logout')
    },

    reloadSettings() {
      new Data(this.$api).reInit()
    },
    onChangeLocale(lang) {
      new Settings(this.$api).change_lang(lang)
    },

    onRefToolbar(refToolbar) {
      this.refToolbar = refToolbar
    },

  }
}
</script>

<style lang="scss">
// body .accent--text {
//   color: #1976D2 !important;
// }
.dialog-z1000 {
  z-index: 1000;
}

.user {
  .v-text-field .v-label {
    // update material input labels for Firefox
    // bottom: 0;
    // top: auto;
    // fix labels for multi-line inputs (ditch Firefox fix)
    top: 8px;
  }
  .v-text-field__slot {
    height: 2rem;
  }
}
</style>

<style lang="scss" scoped>
.v-main {
  margin-top: 10px;
}
.v-alert {
  margin: 0;
}
.v-toolbar {
  background-color: #ffffff;
}
.v-list--dense .v-subheader {
  background: whitesmoke;
}
</style>
